import { Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import { MagazineIcon, TwoPiledSquaresIcon } from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';

import { Divider, ResponsiveFloraTypography } from 'components';
import { CATALOGS } from 'config/environments/shared';
import { copyText, getMFImagesLink } from 'helpers';
import { useUrlTrackingParams, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './CardCatalog.styles';
import { CatalogCycleInfos } from '../CatalogCycleInfo';

import type { CardCatalogProps } from './CardCatalog.d';

export const CardCatalog = ({
  banner,
  brand,
  cycleId,
  cycleEnd,
  cycleNumber,
  cycleStart,
  cycleYear,
  link,
  isNextCycle,
  index = 0,
  name,
}: CardCatalogProps) => {
  const linkWithTrackingParams = useUrlTrackingParams(link || '');
  const { sendDataToGTM } = useTracking();
  const cycleCurrent = cycleId || cycleNumber?.toString();

  const handleCopy = () => {
    trackClick('copiar-link');
    copyText(linkWithTrackingParams)
      .then(() => {
        Toaster.notify({
          kind: 'success',
          description: `Link copiado com sucesso!`,
          origin: 'right-top',
        });
      })
      .catch((e) => {
        Toaster.notify({
          kind: 'error',
          description: `Não foi possível copiar o link. Por favor, tente novamente.`,
          origin: 'right-top',
        });

        newRelicMonitoringService.addPageAction('callback_copiar_link', {
          cd_detail: `erro:${e.message}`,
        });
      });
  };

  const handleLinkRedirect = () => {
    trackClick('abrir-catalogo');
  };

  const trackClick = (label: string) => {
    const idCycle = isNextCycle ? `${cycleYear}${cycleNumber}` : cycleId || cycleNumber;

    sendDataToGTM({
      event: 'select_promotion',
      ecommerce: {
        creative_name: linkWithTrackingParams,
        creative_slot: `${index + 1}`,
        promotion_id: `${brand?.toLowerCase()}-${idCycle}`,
        promotion_name: name || brand,
      },
      event_parameters: {
        cd_interaction_detail: `click:${label}`,
        cd_section: isNextCycle ? 'proximo-ciclo' : 'ciclo-atual',
      },
    });
  };

  return (
    <Flex direction='column' gap='$3'>
      <Styles.Card>
        <Styles.Grid>
          <Styles.CardImage
            src={`${banner || `${getMFImagesLink()}mld-${brand?.toLowerCase()}.png`}`}
            alt={`Imagem do catálogo do ciclo de ${name || brand} `}
            css={{ gridArea: 'image' }}
          />

          <Flex css={{ gridArea: 'content' }} direction='column' gap='$2'>
            <ResponsiveFloraTypography fontSize='title' as='h1'>
              <Styles.LogoImage
                src={`${getMFImagesLink()}logo-${brand?.toLowerCase()}.svg`}
                alt={`Seção do catálogo de ${brand}`}
              />
            </ResponsiveFloraTypography>

            <Divider variant='Line' />
            <CatalogCycleInfos
              cycleEnd={cycleEnd}
              cycleId={cycleCurrent}
              cycleStart={cycleStart}
              isNextCycle={isNextCycle}
              isUniqueLink={brand?.toLocaleLowerCase() === CATALOGS.MULTIBRAND}
            />
          </Flex>

          <Flex css={{ gridArea: 'action' }} data-wrapper='buttons' direction='column' gap='$2'>
            {!isNextCycle && (
              <FloraButton
                onClick={handleCopy}
                has='iconLeft'
                icon={<TwoPiledSquaresIcon />}
                hierarchy='primary'
                isFull
                aria-label={`Copiar link do catálogo de ${brand}`}
              >
                <ResponsiveFloraTypography fontWeight='medium'>
                  Copiar link
                </ResponsiveFloraTypography>
              </FloraButton>
            )}

            <FloraButton
              as={Link}
              onClick={handleLinkRedirect}
              to={linkWithTrackingParams}
              target='_blank'
              rel='noopener noreferrer'
              has='iconLeft'
              icon={<MagazineIcon />}
              hierarchy={isNextCycle ? 'primary' : 'secondary'}
              isFull
              aria-label={`Abrir catálogo de ${brand}`}
            >
              <ResponsiveFloraTypography fontWeight='medium'>
                Abrir catálogo
              </ResponsiveFloraTypography>
            </FloraButton>
          </Flex>
        </Styles.Grid>
      </Styles.Card>
    </Flex>
  );
};
